import React, { useState, useEffect, useRef } from 'react';
import { Card, ProgressBar, Badge, Button, Collapse, Modal, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faMagic,
  faCheck,
  faSpinner,
  faTimes,
  faExclamationTriangle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { useEnrichment } from 'context/EnrichmentContext';
import './EnrichmentProgressPanel.css';

/**
 * Labels for enrichment modes.
 */
const MODE_LABELS = {
  title: 'Title',
  description: 'Description',
  metadata: 'Metadata',
  weight: 'Weight',
  tagging: 'Tags',
  seo: 'SEO'
};

/**
 * Status configuration for badges.
 */
const STATUS_CONFIG = {
  pending: { icon: faClock, color: 'secondary', label: 'Pending' },
  processing: { icon: faSpinner, color: 'primary', label: 'Processing', spin: true },
  completed: { icon: faCheck, color: 'success', label: 'Complete' },
  failed: { icon: faTimes, color: 'danger', label: 'Failed' },
  partial: { icon: faExclamationTriangle, color: 'warning', label: 'Partial' }
};

/**
 * EnrichmentProgressPanel - Collapsible panel showing enrichment progress.
 *
 * Features:
 * - Batch-level progress (X/Y products)
 * - Mode-level status per product (T, D, M, W badges)
 * - Cancel with confirmation
 * - Auto-collapse 30s after completion
 * - Dismiss when done
 */
const EnrichmentProgressPanel = () => {
  const { jobs, isProcessing, progress, cancelEnrichment, clearEnrichment } = useEnrichment();
  const [collapsed, setCollapsed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const autoCollapseTimerRef = useRef(null);

  const jobList = Object.values(jobs);
  const hasJobs = jobList.length > 0;
  const percentage = progress.total > 0
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;

  // Auto-collapse 30 seconds after completion
  useEffect(() => {
    if (!isProcessing && hasJobs && progress.completed === progress.total) {
      autoCollapseTimerRef.current = setTimeout(() => {
        setCollapsed(true);
      }, 30000);
    }

    return () => {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current);
      }
    };
  }, [isProcessing, hasJobs, progress.completed, progress.total]);

  // Handle cancel confirmation
  const handleCancelConfirm = async () => {
    await cancelEnrichment();
    setShowCancelModal(false);
  };

  /**
   * Get mode status for a specific job and mode.
   */
  const getModeStatus = (job, mode) => {
    if (!job.modes?.includes(mode)) return null;
    if (job.results?.[mode]?.status) return job.results[mode].status;
    if (job.progress?.current === mode) return 'processing';
    return 'pending';
  };

  // Don't render if no jobs
  if (!hasJobs) {
    return null;
  }

  return (
    <>
      <Card className={`enrichment-progress-panel mb-3 mx-3 mt-3 ${collapsed ? 'collapsed' : ''}`}>
        <Card.Header
          className="d-flex justify-content-between align-items-center cursor-pointer"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faMagic}
              className={`me-2 text-primary ${isProcessing ? 'fa-pulse' : ''}`}
            />
            <span className="fw-bold">
              {isProcessing ? 'Enriching' : 'Enrichment Complete'}
            </span>
            <Badge bg={isProcessing ? 'primary' : progress.failed > 0 ? 'warning' : 'success'} className="ms-2">
              {progress.completed}/{progress.total}
            </Badge>
            {progress.failed > 0 && (
              <Badge bg="danger" className="ms-1">{progress.failed} failed</Badge>
            )}
          </div>
          <div className="d-flex align-items-center">
            {isProcessing && (
              <Button
                variant="outline-danger"
                size="sm"
                className="me-2"
                onClick={(e) => { e.stopPropagation(); setShowCancelModal(true); }}
              >
                Cancel
              </Button>
            )}
            {!isProcessing && (
              <Button
                variant="link"
                size="sm"
                className="me-2 text-muted"
                onClick={(e) => { e.stopPropagation(); clearEnrichment(); }}
              >
                Dismiss
              </Button>
            )}
            <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
          </div>
        </Card.Header>

        <Collapse in={!collapsed}>
          <Card.Body>
            {/* Overall progress bar */}
            <ProgressBar
              now={percentage}
              label={`${percentage}%`}
              variant={isProcessing ? 'primary' : progress.failed > 0 ? 'warning' : 'success'}
              animated={isProcessing}
              className="mb-3"
            />

            {/* Show details toggle */}
            <Button
              variant="link"
              size="sm"
              className="p-0 mb-2"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Hide details' : 'Show details'} ({jobList.length} products)
            </Button>

            {/* Product-level details */}
            <Collapse in={showDetails}>
              <div className="product-details-table">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Status</th>
                      <th>Modes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobList.slice(0, 20).map(job => {
                      const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
                      return (
                        <tr key={job.sku} className={job.status === 'failed' ? 'table-danger' : ''}>
                          <td>
                            <code className="small">{job.sku}</code>
                          </td>
                          <td>
                            <Badge bg={statusConfig.color}>
                              <FontAwesomeIcon
                                icon={statusConfig.icon}
                                spin={statusConfig.spin}
                                className="me-1"
                              />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td>
                            {job.modes?.map(mode => {
                              const modeStatus = getModeStatus(job, mode);
                              const modeConfig = STATUS_CONFIG[modeStatus] || STATUS_CONFIG.pending;
                              return (
                                <Badge
                                  key={mode}
                                  bg={modeConfig.color}
                                  className="me-1"
                                  title={`${MODE_LABELS[mode] || mode}: ${modeConfig.label}`}
                                >
                                  {MODE_LABELS[mode]?.charAt(0) || mode.charAt(0).toUpperCase()}
                                </Badge>
                              );
                            })}
                          </td>
                        </tr>
                      );
                    })}
                    {jobList.length > 20 && (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">
                          ... and {jobList.length - 20} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </Card.Body>
        </Collapse>
      </Card>

      {/* Cancel confirmation modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Enrichment?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel enrichment?</p>
          <p className="text-muted mb-0">
            {progress.total - progress.completed} products won't be processed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Keep Running
          </Button>
          <Button variant="danger" onClick={handleCancelConfirm}>
            Stop Enrichment
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EnrichmentProgressPanel;
